import {
    memo,
    useCallback,
    useDeferredValue,
    useMemo,
    type ReactNode
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
    DataGridPro,
    naturalSortComparator,
    type GridColDef,
    type GridFilterModel,
    type GridListViewColDef,
    type GridRowHeightParams,
    type GridSortModel
} from '../../lib/dataGrid';
import SearchField from '../../components/SearchField';
import FieldSortButton from './FieldSortButton';
import FieldCardPreview from './FieldCardPreview';
import {
    useFieldListSort,
    type FieldSortOption,
    type SortDirection
} from './fieldListSort';

export interface FieldCardRow {
    id: string;
    /** The bold first line. */
    title: ReactNode;
    subtitle?: string;
    /** Pushed to the right of the title (the Tagebuch day's worked hours). */
    trailing?: ReactNode;
    highlighted?: boolean;
    /** What the list is ordered by — natural, numeric-aware. */
    sortKey: string;
    /**
     * Every order the list offers, by the options' keys — only for a list with
     * a sort button (see `sort`).
     */
    sortValues?: Record<string, string>;
    /** What the search matches against; defaults to `sortKey`. */
    searchText?: string;
    /** A picture at the start of the card, by media id and `updatedAt`. */
    previewMediaId?: string;
    previewVersion?: string;
}

/**
 * Never displayed — the grid renders one column, the card. These exist so the
 * list has an order and the quick filter has values to match.
 */
const BASE_COLUMNS: GridColDef<FieldCardRow>[] = [
    { field: 'sortKey', sortComparator: naturalSortComparator },
    { field: 'searchText' }
];

/** The hidden column a sort option orders by. */
const sortField = (key: string) => `sort_${key}`;

/** Stands in for the options of a list without a sort button. */
const NO_SORT_OPTIONS: FieldSortOption[] = [{ key: 'sortKey', labelId: '' }];

const AUTO_ROW_HEIGHT = () => 'auto' as const;

/**
 * Rough card geometry in px, for `getEstimatedRowHeight` only — a close
 * estimate keeps the scrollbar steady over rows that have not rendered yet.
 */
const CARD_HEIGHT = 72;
const SUBTITLE_HEIGHT = 20;
/** Vertical gap between cards, applied as the grid cell's bottom padding. */
const CARD_GAP = 16;

function estimateCardHeight({ model }: GridRowHeightParams): number {
    const row = model as FieldCardRow;
    return CARD_HEIGHT + (row.subtitle ? SUBTITLE_HEIGHT : 0) + CARD_GAP;
}

/**
 * `memo`'d because the grid re-renders its visible rows on every keystroke and
 * RxDB emission — so `onSelect` has to be stable (a `useCallback` in the
 * caller), or every card re-renders anyway.
 */
const FieldCard = memo(function FieldCard({
    id,
    title,
    subtitle,
    trailing,
    highlighted,
    previewMediaId,
    previewVersion,
    onSelect
}: FieldCardRow & { onSelect: (id: string) => void }) {
    return (
        <Card
            variant="outlined"
            sx={
                highlighted
                    ? { borderColor: 'primary.main', borderWidth: 2 }
                    : undefined
            }
        >
            <CardActionArea
                onClick={() => onSelect(id)}
                sx={{ display: 'flex', alignItems: 'stretch' }}
            >
                <CardContent sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: 1
                        }}
                    >
                        <Typography variant="h6">{title}</Typography>
                        {trailing}
                    </Box>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </CardContent>
                {previewMediaId && (
                    <FieldCardPreview
                        mediaId={previewMediaId}
                        version={previewVersion ?? ''}
                    />
                )}
            </CardActionArea>
        </Card>
    );
});

/**
 * The field tool's card lists (Tagebuch days, Gesamtbeschreibungen, projects) as
 * rows of a list-view DataGrid: a plain stack of cards mounts every record at
 * once, and only the grid's row virtualisation keeps that off the DOM. The grid
 * owns the ordering and the search too.
 *
 * ⚠ Needs a parent that is a flex column with a DEFINITE height — otherwise the
 * grid sizes its viewport to all of its rows and virtualises nothing. Under
 * FieldLayout that comes for free; inside a Dialog give `DialogContent` a
 * `display: flex; flex-direction: column`.
 */
export default function FieldCardList({
    rows,
    onSelect,
    emptyMessageId,
    noMatchesMessageId,
    sortDirection = 'asc',
    sort,
    search,
    onSearchChange,
    toolbar
}: {
    rows: FieldCardRow[];
    /** Must be stable (`useCallback`) — see FieldCard. */
    onSelect: (id: string) => void;
    emptyMessageId: string;
    /** Shown when a search hid everything; required with `onSearchChange`. */
    noMatchesMessageId?: string;
    /** The fixed order of a list without `sort`. */
    sortDirection?: SortDirection;
    /**
     * Offers a sort button beside the search, remembered under `listId`. The
     * first option is the list's usual order. `options` must be stable.
     */
    sort?: { listId: string; options: readonly FieldSortOption[] };
    /** Omit both to render the list without a search box. */
    search?: string;
    onSearchChange?: (value: string) => void;
    /** Rendered above the search box — in practice the Add button. */
    toolbar?: ReactNode;
}) {
    const intl = useIntl();
    const searchValue = search ?? '';
    // Deferred so a keystroke paints the new character immediately and the grid
    // re-filters behind it, instead of the two competing for the same frame.
    const searchTerm = useDeferredValue(searchValue);

    const listViewColumn = useMemo<GridListViewColDef<FieldCardRow>>(
        () => ({
            field: 'card',
            renderCell: ({ row }) => <FieldCard {...row} onSelect={onSelect} />
        }),
        [onSelect]
    );

    const sortOptions = sort?.options ?? NO_SORT_OPTIONS;
    const [listSort, setListSort] = useFieldListSort(
        sort?.listId ?? '',
        sortOptions
    );
    const columns = useMemo<GridColDef<FieldCardRow>[]>(
        () => [
            ...BASE_COLUMNS,
            ...(sort?.options ?? []).map(
                (option): GridColDef<FieldCardRow> => ({
                    field: sortField(option.key),
                    valueGetter: (_value, row) =>
                        row.sortValues?.[option.key] ?? '',
                    sortComparator: naturalSortComparator
                })
            )
        ],
        [sort?.options]
    );
    const sortModel = useMemo<GridSortModel>(() => {
        if (!sort) {
            return [{ field: 'sortKey', sort: sortDirection }];
        }
        const usual = sort.options[0];
        return [
            { field: sortField(listSort.key), sort: listSort.direction },
            // Equal values (a day with several Proben) keep the usual order.
            ...(usual.key === listSort.key
                ? []
                : [
                      {
                          field: sortField(usual.key),
                          sort: usual.direction ?? 'asc'
                      }
                  ])
        ];
    }, [sort, sortDirection, listSort.key, listSort.direction]);

    const filterModel = useMemo<GridFilterModel>(
        () => ({
            items: [],
            quickFilterValues: searchTerm.trim().split(/\s+/).filter(Boolean)
        }),
        [searchTerm]
    );

    const hasSearch = searchTerm.trim() !== '';
    const slots = useMemo(
        () => ({
            noRowsOverlay: () => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        p: 2,
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ color: 'text.secondary' }}>
                        <FormattedMessage
                            id={
                                hasSearch && noMatchesMessageId
                                    ? noMatchesMessageId
                                    : emptyMessageId
                            }
                        />
                    </Typography>
                </Box>
            )
        }),
        [hasSearch, noMatchesMessageId, emptyMessageId]
    );

    const handleSearchChange = useCallback(
        (value: string) => onSearchChange?.(value),
        [onSearchChange]
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {(toolbar || onSearchChange) && (
                <Stack spacing={2} sx={{ pb: 2 }}>
                    {toolbar}
                    {onSearchChange && (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                        >
                            <SearchField
                                size="medium"
                                fullWidth
                                value={searchValue}
                                onChange={(event) =>
                                    handleSearchChange(event.target.value)
                                }
                                onClear={() => handleSearchChange('')}
                                autoComplete="off"
                                placeholder={intl.formatMessage({
                                    id: 'grid.search'
                                })}
                                aria-label={intl.formatMessage({
                                    id: 'grid.search'
                                })}
                            />
                            {sort && (
                                <FieldSortButton
                                    options={sort.options}
                                    sort={listSort}
                                    onSort={setListSort}
                                />
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
            <DataGridPro
                rows={rows}
                columns={columns}
                listView
                listViewColumn={listViewColumn}
                filterModel={filterModel}
                sortModel={sortModel}
                getRowHeight={AUTO_ROW_HEIGHT}
                getEstimatedRowHeight={estimateCardHeight}
                disableRowSelectionOnClick
                columnHeaderHeight={0}
                hideFooter
                slots={slots}
                sx={{
                    // The card is the row's whole appearance: no grid border,
                    // header, separators or focus ring. The cell's bottom
                    // padding is the gap between cards.
                    border: 0,
                    backgroundColor: 'transparent',
                    '& .MuiDataGrid-columnHeaders': { display: 'none' },
                    '& .MuiDataGrid-cell': {
                        padding: 0,
                        paddingBottom: `${CARD_GAP}px`,
                        borderTop: 0,
                        '&:focus, &:focus-within': { outline: 'none' }
                    },
                    '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered':
                        {
                            backgroundColor: 'transparent'
                        }
                }}
            />
        </Box>
    );
}
